import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ExternalLink, ArrowUpRight, MapPin, Mail, Globe, QrCode } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Footer = () => {
  const { t, language } = useLanguage();
  const [qrSiteUrl, setQrSiteUrl] = useState('');
  const [qrCarteUrl, setQrCarteUrl] = useState('');

  useEffect(() => {
    fetch(`${API}/qr-code?url=${encodeURIComponent('https://www.bicenter-consulting.com')}&size=400`)
      .then(res => res.blob())
      .then(blob => setQrSiteUrl(URL.createObjectURL(blob)))
      .catch(() => {});
    fetch(`${API}/qr-code?url=${encodeURIComponent('https://www.bicenter-consulting.com/carte-visite')}&size=400`)
      .then(res => res.blob())
      .then(blob => setQrCarteUrl(URL.createObjectURL(blob)))
      .catch(() => {});
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { key: 'home', section: 'hero' },
    { key: 'services', section: 'services' },
    { key: 'about', section: 'about' },
    { key: 'testimonials', section: 'testimonials' },
    { key: 'contact', section: 'contact' }
  ];

  return (
    <footer className="bg-[#004d40] text-white" data-testid="footer">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h3 className="font-serif text-3xl font-bold tracking-tight">
                BICENTER
              </h3>
              <span className="text-[#4caf50] font-semibold text-xl">CONSULTING</span>
              <p className="text-white/60 text-sm mt-1 italic">{t('footer.tagline')}</p>
            </div>
            <p className="text-white/80 leading-relaxed max-w-md mb-6">
              {t('footer.description')}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-white/80">
                <MapPin className="w-4 h-4 text-[#4caf50]" />
                <span className="text-sm">{t('contact.info.address')}</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Mail className="w-4 h-4 text-[#4caf50]" />
                <span className="text-sm">{t('contact.info.email')}</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Globe className="w-4 h-4 text-[#4caf50]" />
                <span className="text-sm">{t('contact.info.website')}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  <button
                    onClick={() => scrollToSection(link.section)}
                    className="text-white/70 hover:text-[#4caf50] transition-colors duration-200 text-sm"
                    data-testid={`footer-link-${link.key}`}
                  >
                    {t(`nav.${link.key}`)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Bicenter Connect */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white">{t('footer.join')}</h4>
            <p className="text-white/70 text-sm mb-6">
              {t('footer.joinDesc')}
            </p>
            <a
              href="https://www.bicenter.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#4caf50] hover:bg-[#388e3c] text-white px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 group"
              data-testid="bicenter-connect-link"
            >
              {t('footer.joinCta')}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </a>

            {/* QR Codes */}
            <div className="mt-8 pt-6 border-t border-white/10 space-y-4">
              {/* QR Site Internet */}
              <a
                href="/qrcode"
                className="flex items-start gap-3 group"
                data-testid="footer-qrcode-site-link"
              >
                <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
                  {qrSiteUrl ? (
                    <img
                      src={qrSiteUrl}
                      alt="QR Code Site Internet"
                      className="w-16 h-16 object-contain"
                      data-testid="footer-qrcode-site-image"
                    />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center">
                      <QrCode className="w-8 h-8 text-[#004d40]" />
                    </div>
                  )}
                </div>
                <div className="pt-1">
                  <p className="text-white/90 text-xs font-semibold group-hover:text-[#4caf50] transition-colors">
                    QR Code Site Internet
                  </p>
                  <p className="text-white/50 text-[10px] mt-0.5">
                    {language === 'fr' ? 'Scanner pour visiter le site' : 'Scan to visit the website'}
                  </p>
                </div>
              </a>

              {/* QR Carte de Visite */}
              <a
                href="/carte-visite"
                className="flex items-start gap-3 group"
                data-testid="footer-qrcode-carte-link"
              >
                <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
                  {qrCarteUrl ? (
                    <img
                      src={qrCarteUrl}
                      alt="QR Code Carte de Visite"
                      className="w-16 h-16 object-contain"
                      data-testid="footer-qrcode-carte-image"
                    />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center">
                      <QrCode className="w-8 h-8 text-[#004d40]" />
                    </div>
                  )}
                </div>
                <div className="pt-1">
                  <p className="text-white/90 text-xs font-semibold group-hover:text-[#4caf50] transition-colors">
                    QR Code Carte de Visite
                  </p>
                  <p className="text-white/50 text-[10px] mt-0.5">
                    {language === 'fr' ? 'Scanner pour ma carte de visite digitale' : 'Scan for my digital business card'}
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} Bicenter Consulting. {t('footer.rights')}.
          </p>
          <p className="text-white/60 text-sm flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            {t('footer.consulting100')}
          </p>
        </div>
      </div>
    </footer>
  );
};
